import { useMemo } from "preact/hooks"
import { useRoute } from "wouter-preact"
import { marked } from "marked"
import { find_guide, help_guides } from "./guides"
import { classes } from "@/utils"

marked.setOptions({ gfm: true })

/** `/help` lists the guides; `/help/:slug` renders one with a table of contents on the left. */
export function HelpPage() {
  const [, params] = useRoute("/help/:slug")
  const guide = params?.slug ? find_guide(params.slug) : undefined

  return (
    <div class="mx-auto flex max-w-6xl gap-8 px-4 py-6">
      <nav class="hidden w-56 shrink-0 md:block">
        <h2 class="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Guides</h2>
        <ul class="space-y-1">
          {help_guides.map((g) => (
            <li key={g.slug}>
              <a
                href={`/help/${g.slug}`}
                class={classes(
                  "block rounded px-2 py-1 text-sm hover:bg-gray-100",
                  guide?.slug === g.slug ? "bg-gray-100 font-medium text-gray-900" : "text-gray-600",
                )}
              >
                {g.title}
              </a>
            </li>
          ))}
        </ul>
      </nav>
      <div class="min-w-0 flex-1">{guide ? <GuideArticle slug={guide.slug} body={guide.body} /> : <GuideIndex />}</div>
    </div>
  )
}

function GuideIndex() {
  return (
    <div>
      <h1 class="mb-1 text-2xl font-semibold text-gray-900">Help</h1>
      <p class="mb-6 text-sm text-gray-600">
        How MarginWiz works, workflow by workflow. The chat bubble in the bottom right reaches the team for anything
        not covered here.
      </p>
      <ul class="grid gap-3 sm:grid-cols-2">
        {help_guides.map((g) => (
          <li key={g.slug}>
            <a href={`/help/${g.slug}`} class="block rounded-lg border border-gray-200 p-4 hover:border-gray-300 hover:bg-gray-50">
              <div class="font-medium text-gray-900">{g.title}</div>
              <div class="mt-1 text-sm text-gray-600">{g.summary}</div>
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** Rewrite relative guide links (`(cash-application)`) to `/help/<slug>` so cross-references work in the app. */
export function render_guide_html(body: string): string {
  const linked = body.replace(/\]\(([a-z0-9-]+)\)/g, "](/help/$1)")
  return marked.parse(linked) as string
}

/** One guide's markdown rendered as an article. Also embedded in Settings › API Keys. */
export function GuideArticle({ slug, body, className }: { slug: string; body: string; className?: string }) {
  const html = useMemo(() => render_guide_html(body), [body])
  return (
    <article
      key={slug}
      class={classes(
        "max-w-none text-sm leading-6 text-gray-700",
        "[&_h1]:mb-3 [&_h1]:text-2xl [&_h1]:font-semibold [&_h1]:text-gray-900",
        "[&_h2]:mb-2 [&_h2]:mt-8 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-gray-900",
        "[&_h3]:mb-1 [&_h3]:mt-5 [&_h3]:font-semibold [&_h3]:text-gray-900",
        "[&_p]:mb-3 [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1",
        "[&_a]:text-blue-600 [&_a]:underline [&_code]:rounded [&_code]:bg-gray-100 [&_code]:px-1 [&_strong]:text-gray-900",
        "[&_table]:mb-4 [&_table]:w-full [&_table]:border-collapse [&_th]:border-b [&_th]:border-gray-200 [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_th]:font-semibold",
        "[&_td]:border-b [&_td]:border-gray-100 [&_td]:px-2 [&_td]:py-1 [&_td]:align-top",
        "[&_pre]:mb-4 [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-gray-50 [&_pre]:p-3 [&_pre_code]:bg-transparent [&_pre_code]:p-0",
        className,
      )}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}
